import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Popper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import React, { useEffect, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

const CustomLocationInput = ({
  handleInputValueChange,
  componentLabel,
  required = false,
  selectedPlace = "",
  field,
}) => {
  const [query, setQuery] = useState(selectedPlace);
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const autocompleteService = useRef(null);
  const [loading, setLoading] = useState(false);

  const anchorRef = useRef(null); // Reference for the Popper

  const handleInputChange = (event) => {
    setQuery(event.target.value);
  };

  useEffect(() => {
    if (window.google?.maps) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
      return; // API already loaded, exit early
    }

    // Dynamically load the Google Maps API with the provided API key
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => {
      if (window.google?.maps) {
        autocompleteService.current =
          new window.google.maps.places.AutocompleteService();
      }
    };
    script.onerror = () => {
      console.error("Google Maps API failed to load.");
    };

    // Append the script tag only if it's not already in the document head
    if (!document.querySelector(`script[src='${script.src}']`)) {
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup on component unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Fetch place suggestions when the query changes
  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    setLoading(true);

    if (autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        {
          input: query,
          types: ["(cities)"],
        }, // Optional: filter for cities in India
        (predictions, status) => {
          if (
            window.google?.maps?.places?.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
          setLoading(false);
        }
      );
    }
  }, [query]);

  // Handle clearing the input
  const handleClear = () => {
    setQuery("");
    setSuggestions([]);
    handleInputValueChange("Birth Details", field, "");
  };

  // Handle when a user selects a suggestion
  const handleSuggestionSelect = (suggestion) => {
    handleInputValueChange("Birth Details", field, suggestion);
    setQuery(suggestion);
    setSuggestions([]);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack direction={"column"} spacing={0.5} ref={anchorRef}>
        {componentLabel && (
          <Stack direction="row" spacing={0.4} sx={{ mb: 0.3 }}>
            <Typography
              sx={{
                mb: 1,
                fontSize: "14px",
                fontWeight: 500,
                fontFamily: "Roboto",
              }}
            >
              {componentLabel}
            </Typography>
            <Typography
              sx={{
                color: "error.main",
                fontSize: "14px",
                fontWeight: 500,
                fontFamily: "Roboto",
              }}
            >
              {required && "*"}
            </Typography>
          </Stack>
        )}
        <TextField
          required={true}
          sx={{ mb: 0, pb: 0.5, mt: 0, pt: 0 }}
          placeholder={
            componentLabel === "Location"
              ? "Search"
              : "Enter your location here"
          }
          variant="outlined"
          fullWidth
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {loading ? (
                  <CircularProgress size={20} />
                ) : query || isFocused ? (
                  <IconButton onClick={handleClear}>
                    <ClearIcon />
                  </IconButton>
                ) : (
                  <SearchIcon />
                )}
              </InputAdornment>
            ),
          }}
        />
        <Popper
          open={isFocused && suggestions.length > 0}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{
            width: anchorRef.current?.offsetWidth || "100%", // Match TextField width
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              border: "1px solid #ccc",
              borderRadius: "4px",
              backgroundColor: "white",
              maxHeight: "200px",
              overflowY: "auto",
              mt: 1,
              boxShadow: 3,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 2,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : (
              suggestions.map((suggestion) => (
                <Typography
                  key={suggestion.place_id}
                  variant="body2"
                  sx={{
                    padding: "8px",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                  onMouseDown={() => {
                    handleSuggestionSelect(suggestion.description);
                  }}
                >
                  {suggestion.description}
                </Typography>
              ))
            )}
          </Box>
        </Popper>
      </Stack>
    </LocalizationProvider>
  );
};

export default CustomLocationInput;
